<template>
    <Teleport to="body">
        <div
            v-if="isOpen"
            class="absolute top-[40%] left-[40%]"
            :style="{
                zIndex: zIndex ?? 1000,
                width: width ?? '470px',
                height: height ?? '415px',
            }"
            ref="dialogRef"
        >
            <div
                class="
                    bg-white
                    rounded-lg
                    shadow-lg
                    relative
                    font-sans
                    overflow-hidden
                    h-full
                "
            >
                <header
                    class="
                        flex
                        items-center
                        bg-[#2e8ab2]
                        px-4
                        py-2
                        text-white
                        cursor-move
                    "
                    v-if="title"
                    ref="headerRef"
                    @mousedown="startMove"
                    @touchstart="startMove"
                >
                    <div class="flex gap-2 align-middle items-center">
                        <img
                            src="/default-images/icons/whistle-dark-blue.svg"
                            alt="Whistle"
                            width="24"
                        />
                        <h3 class="mr-12">{{ title }}</h3>
                    </div>
                    <button
                        type="button"
                        class="
                            rounded-md
                            ml-auto
                            leading-[0]
                            p-1
                            hover:bg-[#68bfe5]
                        "
                        @click="$emit('close')"
                        @touchstart="$emit('close')"
                        v-if="!hideCloseButton"
                    >
                        <ion-icon name="close"></ion-icon>
                    </button>
                </header>
                <slot></slot>
                <div
                    class="
                        absolute
                        w-10
                        h-10
                        bottom-[-2px]
                        right-[-2px]
                        cursor-nwse-resize
                    "
                    @mousedown="startResize"
                    @touchstart="startResize"
                ></div>
            </div>
        </div>
    </Teleport>
</template>

<script setup>
import { ref } from "vue";

const props = defineProps([
    "title",
    "isOpen",
    "zIndex",
    "width",
    "height",
    "hideCloseButton",
]);
let dialogRef = ref(null);

function startMove(e) {
    e = e || window.event;
    e.preventDefault();

    // Handle both mouse and touch events
    const isTouch = e.type.startsWith("touch");
    let prevX = isTouch ? e.touches[0].clientX : e.clientX;
    let prevY = isTouch ? e.touches[0].clientY : e.clientY;
    let isDragging = false;

    function mousemove(e) {
        e = e || window.event;
        e.preventDefault();

        const isTouchMove = e.type.startsWith("touch");
        const currentX = isTouchMove ? e.touches[0].clientX : e.clientX;
        const currentY = isTouchMove ? e.touches[0].clientY : e.clientY;

        // Calculate the difference in position
        const newX = prevX - currentX;
        const newY = prevY - currentY;

        const rect = dialogRef.value.getBoundingClientRect();
        dialogRef.value.style.left = rect.left - newX + "px";
        dialogRef.value.style.top = rect.top - newY + "px";

        // Update the previous position
        prevX = currentX;
        prevY = currentY;
    }

    function mouseup() {
        isDragging = false;

        // Remove event listeners
        window.removeEventListener("mousemove", mousemove);
        window.removeEventListener("mouseup", mouseup);
        window.removeEventListener("touchmove", mousemove);
        window.removeEventListener("touchend", mouseup);
        window.removeEventListener("touchcancel", mouseup);
    }

    window.requestAnimationFrame(() => {
        window.addEventListener("mousemove", mousemove);
        window.addEventListener("touchmove", mousemove);
    });

    // Add the event listeners
    window.addEventListener("mousemove", mousemove);
    window.addEventListener("touchmove", mousemove);
    window.addEventListener("mouseup", mouseup);
    window.addEventListener("touchend", mouseup);
    window.addEventListener("touchcancel", mouseup);

    isDragging = true;
}

function startResize(e) {
    e = e || window.event;
    e.preventDefault();

    // Handle both mouse and touch events
    const isTouch = e.type.startsWith("touch");
    let prevX = isTouch ? e.touches[0].clientX : e.clientX;
    let prevY = isTouch ? e.touches[0].clientY : e.clientY;
    let isResizing = false;

    function mousemove(e) {
        e = e || window.event;
        e.preventDefault();

        if (!isResizing) {
            return;
        }

        // Detect if it's a touch event or mouse event
        const isTouchMove = e.type.startsWith("touch");
        const currentX = isTouchMove ? e.touches[0].clientX : e.clientX;
        const currentY = isTouchMove ? e.touches[0].clientY : e.clientY;

        // Calculate the difference in position
        const deltaX = prevX - currentX;
        const deltaY = prevY - currentY;

        const rect = dialogRef.value.getBoundingClientRect();
        const newWidth = rect.width - deltaX;
        const newHeight = rect.height - deltaY;

        // Optional constraints (comment out to allow free resizing)
        /*
        if (newWidth <= 120 || newWidth >= 470) {
            return;
        }
        if (newHeight <= 120 || newHeight >= 415) {
            return;
        }
        */

        // Apply new size to the dialog
        dialogRef.value.style.width = newWidth + "px";
        dialogRef.value.style.height = newHeight + "px";

        // Update previous position for the next movement
        prevX = currentX;
        prevY = currentY;
    }

    function mouseup() {
        isResizing = false;

        // Remove event listeners for both mouse and touch events
        window.removeEventListener("mousemove", mousemove);
        window.removeEventListener("mouseup", mouseup);
        window.removeEventListener("touchmove", mousemove);
        window.removeEventListener("touchend", mouseup);
        window.removeEventListener("touchcancel", mouseup);
    }

    // Add event listeners for both mouse and touch
    window.addEventListener("mousemove", mousemove);
    window.addEventListener("touchmove", mousemove);
    window.addEventListener("mouseup", mouseup);
    window.addEventListener("touchend", mouseup);
    window.addEventListener("touchcancel", mouseup);

    isResizing = true;
}
</script>

<style scoped></style>
